import {
  InformativeModal,
  SingleSelect,
  SelectOptionsHook,
  DropdownOption,
  PHARMACY_REVIEW_QUEUE_NAME,
  store,
  useFeature,
} from "@coherehealth/common";
import { useContext, useMemo, useState } from "react";
import { UserContext } from "../../../UserContext";
import { ServiceCaseSearch } from "@coherehealth/qm-api";
import { useLaunchDarklyFeatureFlag } from "../../../hooks/useLaunchDarklyFeatureFlag";
import safeRegex from "safe-regex";

interface QueueSelectionModalProps {
  isOpen: boolean;
  closeModal: () => void;
  setQueryParams?: React.Dispatch<React.SetStateAction<ServiceCaseSearch>>;
}

const QueueSelectionModal = ({ isOpen = false, closeModal = () => {}, setQueryParams }: QueueSelectionModalProps) => {
  const { queues, activeQueue, setActiveQueue, settingActiveQueue } = useContext(UserContext);
  const [currentQueueId, setCurrentQueueId] = useState<string | undefined>(activeQueue?.id);

  const { featureFlagEnabled: enablePharmDReviews } = useLaunchDarklyFeatureFlag({
    featureFlagName: "enablePharmDReviews",
  });

  const visibleQueues = useMemo(
    () => (enablePharmDReviews ? queues : queues.filter((queue) => queue.name !== PHARMACY_REVIEW_QUEUE_NAME)),
    [queues, enablePharmDReviews]
  );

  const queueOptions: DropdownOption[] = visibleQueues.map((queue) => ({
    id: queue.id || "",
    label: queue.name || "",
  }));
  const useQueueSearch = makeUseQueueSearch(queueOptions);

  const allowFiltersPersistanceOnQueueSection = useFeature("allowFiltersPersistanceOnQueueSection");

  /**
   * This function handles the logic for changing the queue.
   * When the selected queue changes, the relevant query parameters are reset to their defaults,
   * and the state is updated in the store if persistence is enabled.
   */
  const handleQueueChange = async () => {
    if (currentQueueId !== activeQueue?.id) {
      if (allowFiltersPersistanceOnQueueSection) {
        // Save default query params to the store when queue changes only if feature flag is enabled
        store.set("SupervisorDashboardQueryParams", {
          max: 21,
          sortBy: [],
          sortDesc: true,
          offset: 0,
        });
        setQueryParams?.({
          max: 21,
          sortBy: [],
          sortDesc: true,
          offset: 0,
        });
      }

      if (currentQueueId) {
        await setActiveQueue(currentQueueId);
      } else if (activeQueue?.id) {
        await setActiveQueue(activeQueue.id);
      }
    }
    closeModal();
  };

  return (
    <InformativeModal
      open={isOpen}
      onClose={closeModal}
      headerText="Select a queue"
      subHeaderElement={
        <SingleSelect
          label="Select queue"
          useOptions={useQueueSearch}
          selectedValue={queueOptions.find((queue) => queue.id === currentQueueId)}
          setSelectedValue={(queue) => {
            setCurrentQueueId(queue?.id);
          }}
          getOptionLabel={(option) => {
            return isString(option?.label) ? option?.label || "" : "";
          }}
          openOnFocus
          data-testid="qm-dropdown-select-queue"
        />
      }
      primaryButtonTestid="qm-button-go-to-queue"
      primaryButtonText="Go to queue"
      primaryButtonAction={handleQueueChange}
      primaryButtonLoading={settingActiveQueue}
      primaryButtonDisabled={settingActiveQueue}
      tertiaryButtonText={"Cancel"}
      tertiaryButtonAction={closeModal}
      showDivider={false}
    />
  );
};

const isString = (field: any): field is string => {
  return typeof field === "string";
};

const makeUseQueueSearch =
  (fetchedQueueOptions: DropdownOption[]): SelectOptionsHook<DropdownOption | undefined> =>
  ({ query }) => {
    let searchRegex: RegExp;
    if (query) {
      const isRegexSafe = safeRegex(query);
      if (isRegexSafe) {
        searchRegex = new RegExp(query, "i");
      }
    } else {
      searchRegex = new RegExp("", "i");
    }

    const filterOptions = (options: (DropdownOption | undefined)[]) =>
      options.filter((queueOption) => {
        return isString(queueOption?.label) && searchRegex.test(queueOption?.label || "");
      });

    return {
      options: fetchedQueueOptions,
      optionsLoading: false,
      filterOptions,
    };
  };

export default QueueSelectionModal;
